import PropTypes from 'prop-types';
import { analysisActions, useNodeById, useMCPlot } from '@/stores/analysisStore';
import MCPlotForm from './MCPlotForm';
import MCChart from '@/components/charts/MCChart';
import { getMCXaxisLabel } from '@/utils/analysisUtils';
import AnalysisBox from '@/components/core/AnalysisBox';
import { useAnalysisProgress } from '@/stores/analysisStatusStore';
import { isDefined } from '@/utils/miscUtils';
import ProgressBar from '@/components/core/ProgressBar';
import { usePlotSync, usePlotSyncHandler } from '@/components/providers/PlotSyncProvider';
import { useEffect, useRef } from 'react';

const MCPlotPanel = ({ distribution, title }) => {
  const plot = useMCPlot();
  const { results, scalar = 1 } = plot;

  const { histogram, unit } = results[distribution];
  const { values: dataset, bin_edges: labels, kde } = histogram;
  const xAxisLabel = getMCXaxisLabel(distribution, unit, scalar);

  return (
    <AnalysisBox>
      <h3 className="text-lg font-medium mb-2">{title}</h3>
      <MCChart
        data={dataset}
        labels={labels}
        distribution={distribution}
        xAxisLabel={xAxisLabel}
        scalar={scalar}
        kde={kde}
      />
    </AnalysisBox>
  );
};

MCPlotPanel.propTypes = {
  distribution: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const MCPlot = () => {
  const plot = useMCPlot();
  const { nodeId, results } = plot;
  const node = useNodeById(nodeId);
  const { setParams } = usePlotSync();

  const updatePlot = formData => {
    setParams(formData);
    analysisActions.updatePlot('mc', {
      nodeId: formData.nodeId,
      scalar: formData.scalar,
      levelizeBy: formData.levelizeBy,
      levelize: formData.levelize,
    });
  };

  const { syncData } = usePlotSyncHandler(updatePlot, plot);

  const mcProgress = useAnalysisProgress('mc');

  // Track when we first see progress so we can estimate time remaining.
  const progressStartRef = useRef(null);

  useEffect(() => {
    if (isDefined(mcProgress) && mcProgress > 0 && mcProgress < 1) {
      if (progressStartRef.current === null) {
        progressStartRef.current = { time: Date.now(), progress: mcProgress };
      }
    } else {
      progressStartRef.current = null;
    }
  }, [mcProgress]);

  const getRemainingLabel = () => {
    if (!progressStartRef.current || !isDefined(mcProgress)) return null;
    const progressDone = mcProgress - progressStartRef.current.progress;
    if (progressDone <= 0) return null;
    const elapsedSec = (Date.now() - progressStartRef.current.time) / 1000;
    const remaining = Math.round((elapsedSec / progressDone) * (1 - mcProgress));
    if (remaining < 60) return `~${remaining}s remaining`;
    return `~${Math.ceil(remaining / 60)}m remaining`;
  };

  useEffect(() => {
    if (!isDefined(mcProgress)) {
      syncData();
    }
  }, [mcProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isDefined(mcProgress) && mcProgress < 1) {
    const remainingLabel = getRemainingLabel();
    return (
      <AnalysisBox>
        <ProgressBar value={mcProgress * 100} rightLabel={remainingLabel} />
      </AnalysisBox>
    );
  }

  if (!node || !results) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 relative">
      <div>
        <MCPlotForm
          key={plot.nodeId}
          onSubmit={updatePlot}
          defaultValues={{ ...plot, levelize: plot.levelize ?? (plot.levelizeBy ? 'true' : 'false') }}
        />
      </div>
      <div className="grid gap-4 grid-cols-2">
        <MCPlotPanel distribution="emissions" title="Emissions Distribution" />
        <MCPlotPanel distribution="levelized_cost" title="Cost Distribution" />
      </div>
    </div>
  );
};

export default MCPlot;
